
import React, { useState, useRef } from "react";
import { ConfigTabs } from "@/components/settings/ConfigTabs";
import { ConfigHeader } from "@/components/settings/ConfigHeader";
import { useProfile } from "@/hooks/useProfile";
import { useIsMobile } from "@/hooks/use-mobile";
import { Navigation } from "@/components/layout/Navigation";
import { ProfileLoading } from "@/components/settings/ProfileLoading";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { AlertCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import { IntroTour } from "@/components/intro/IntroTour";
import { TutorialButton } from "@/components/intro/TutorialButton";

const Configuracion = () => {
  const isMobile = useIsMobile();
  const { profile, isLoading, error } = useProfile();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [runTutorial, setRunTutorial] = useState(false);
  const errorShownRef = useRef(false);

  console.log("Configuration page state:", { 
    profile, 
    isLoading, 
    error: error ? {
      message: error.message,
      stack: error instanceof Error ? error.stack : "No stack trace available"
    } : null 
  });

  const handleResetSession = async () => {
    console.log("Resetting session and clearing cache...");
    try {
      queryClient.clear();
      console.log("Query cache cleared");
      
      const { error: signOutError } = await supabase.auth.signOut();
      if (signOutError) {
        console.error("Error signing out:", signOutError);
        throw signOutError;
      }
      console.log("Signed out successfully");
      
      navigate("/auth", { replace: true });
    } catch (error) {
      console.error("Error resetting session:", error);
      if (!errorShownRef.current) {
        toast({
          variant: "destructive",
          title: "Error",
          description: "No se pudo cerrar la sesión. Por favor, intenta de nuevo.",
        });
        errorShownRef.current = true;
        setTimeout(() => {
          errorShownRef.current = false;
        }, 5000);
      }
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-primary/10 via-background to-background text-primary">
        <ConfigHeader />
        <main className="p-8">
          <ProfileLoading />
        </main>
        <Navigation />
      </div>
    );
  }

  if (error) {
    console.error("Error in configuration page:", {
      message: error.message,
      stack: error instanceof Error ? error.stack : "No stack trace available"
    });
    return (
      <div className="min-h-screen bg-gradient-to-b from-primary/10 via-background to-background text-primary">
        <ConfigHeader />
        <main className="p-8">
          <Alert variant="destructive" className="mb-4">
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>Error al cargar el perfil</AlertTitle>
            <AlertDescription className="mt-2">
              <p className="mb-4 text-primary">No se pudo cargar tu perfil. Esto puede deberse a un problema de conexión o a que tu sesión ha expirado.</p>
              <Button 
                variant="destructive" 
                onClick={handleResetSession}
                className="w-full"
              >
                Volver a iniciar sesión
              </Button>
            </AlertDescription>
          </Alert>
        </main>
        <Navigation />
      </div>
    );
  }

  const isAdmin = profile?.role === 'admin';

  return (
    <div className="min-h-screen bg-gradient-to-b from-primary/10 via-background to-background text-primary pb-24 relative">
      <ConfigHeader />
      <TutorialButton onClick={() => setRunTutorial(true)} />
      <main className="p-8">
        <ConfigTabs isAdmin={isAdmin} userRole={profile?.role} />
      </main>
      <Navigation />
      <IntroTour run={runTutorial} setRun={setRunTutorial} />
    </div>
  );
};

export default Configuracion;
